import { useState } from 'react';
import { useNavigate, Navigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Stack,
  Group,
  Button,
  PageHeader,
  ResourceLayout,
  BottomDrawer,
  ConfirmDialog,
  Alert,
  Paper,
  Text,
  MobileListRow,
  RowActions,
  DEFAULT_PAGE_LIMIT,
  formatAddress,
  type ResourceQuery,
} from '@vipso/shared';
import {
  useListCustomersQuery,
  useAddUpdateCustomerMutation,
  useDeleteCustomerMutation,
  type Customer,
} from '../api/tenantApi';
import {
  CustomerForm,
  createEmptyCustomerForm,
  customerFormValues,
  isCustomerPhoneValid,
  customerFormInput,
  type CustomerFormValues,
} from '../features/customers/CustomerForm';
import { useModuleAccess } from '../hooks/useModuleAccess';
import { useApprovalModuleAccess } from '../hooks/useApprovalModuleAccess';
import { useModuleEntitled, useModuleLabel, useModuleTerminology } from '../hooks/useModuleLabel';
import { ApprovalStatusBadge } from '../components/ApprovalStatusBadge';

function customerListTitle(customer: Customer): string {
  const addressSnippet = formatAddress(customer.address);
  if (!addressSnippet) return customer.name;
  // Prefer a short snippet (line1) like mobile "(address)"
  const short =
    customer.address?.line1?.trim() ||
    customer.address?.city?.trim() ||
    addressSnippet;
  return `${customer.name} (${short})`;
}

function customerListSubtitle(customer: Customer, noPhone: string): string {
  const place =
    customer.address?.city?.trim() ||
    customer.address?.line2?.trim() ||
    '';
  const phone = customer.phone?.trim() || '';
  if (place && phone) return `${place} - ${phone}`;
  return place || phone || noPhone;
}

export function CustomersPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const access = useModuleAccess('customer');
  const approvalAccess = useApprovalModuleAccess();
  const moduleLabel = useModuleLabel('customer', 'customers.title');
  const terms = useModuleTerminology('customer');
  const entitled = useModuleEntitled('customer');
  const [query, setQuery] = useState<ResourceQuery>({
    page: 1,
    pageLimit: DEFAULT_PAGE_LIMIT,
    search: '',
  });
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<Customer | null>(null);
  const [form, setForm] = useState<CustomerFormValues>(createEmptyCustomerForm);
  const [phoneError, setPhoneError] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<Customer | null>(null);

  const { data, isLoading, isFetching, isError, refetch } = useListCustomersQuery({
    page: query.page,
    pageLimit: query.pageLimit,
    search: query.search || undefined,
  });
  const [addUpdateCustomer, { isLoading: saving }] = useAddUpdateCustomerMutation();
  const [deleteCustomer, { isLoading: deleting }] = useDeleteCustomerMutation();

  const list = data?.listCustomers;

  function openCreate() {
    setEditing(null);
    setPhoneError(null);
    setForm(createEmptyCustomerForm());
    setModalOpen(true);
  }

  function openEdit(row: Customer) {
    setEditing(row);
    setPhoneError(null);
    setForm(customerFormValues(row));
    setModalOpen(true);
  }

  async function handleSave() {
    if (!isCustomerPhoneValid(form)) {
      setPhoneError(t('phone.invalid'));
      return;
    }
    setPhoneError(null);
    await addUpdateCustomer(customerFormInput(form, editing)).unwrap();
    setModalOpen(false);
  }

  async function handleDelete() {
    if (!confirmDelete) return;
    await deleteCustomer(confirmDelete.id).unwrap();
    setConfirmDelete(null);
    setModalOpen(false);
  }

  if (!entitled) return <Navigate to="/dashboard" replace />;

  return (
    <Stack gap="md">
      <PageHeader
        title={moduleLabel}
        description={t('terminology.manageRecords', { entity: terms.plural })}
      />

      {isError ? (
        <Alert color="red" title={t('terminology.loadFailed', { entity: terms.plural })}>
          {t('common.connectionError')}
        </Alert>
      ) : null}

      <ResourceLayout
        data={list}
        isLoading={isLoading}
        isFetching={isFetching}
        onQueryChange={setQuery}
        getRowId={(row) => row.id}
        searchable
        searchPlaceholder={t('terminology.search', { entity: terms.plural })}
        emptyTitle={t('terminology.emptyTitle', { entity: terms.plural })}
        emptyDescription={t('terminology.emptyDescription', { entity: terms.singular })}
        onRowClick={(row) => navigate(`/customers/${row.id}`)}
        onRefresh={() => void refetch()}
        refreshLabel={t('common.refresh')}
        mobileView="list"
        rowActionsHeader={t('common.actions')}
        renderRowActions={
          access.canUpdate || access.canDelete
            ? (row) => (
                <RowActions
                  editLabel={t('common.edit')}
                  deleteLabel={t('common.delete')}
                  onEdit={access.canUpdate ? () => openEdit(row) : undefined}
                  onDelete={access.canDelete ? () => setConfirmDelete(row) : undefined}
                />
              )
            : undefined
        }
        addButton={
          access.canCreate ? (
            <Button onClick={openCreate}>{t('terminology.add', { entity: terms.singular })}</Button>
          ) : undefined
        }
        columns={[
          { key: 'name', header: t('common.name'), render: (row) => row.name },
          { key: 'phone', header: t('common.phone'), render: (row) => row.phone || t('common.emptyValue') },
          { key: 'email', header: t('common.email'), render: (row) => row.email || t('common.emptyValue') },
          ...(approvalAccess.customer
            ? [
                {
                  key: 'approval',
                  header: t('approval.status'),
                  render: (row: Customer) => (
                    <ApprovalStatusBadge status={row.approvalStatus} />
                  ),
                },
              ]
            : []),
          {
            key: 'balance',
            header: t('customers.openingBalance'),
            render: (row) => row.openingBalance ?? 0,
          },
        ]}
        renderCard={(row: Customer) => (
          <Paper p="md" withBorder radius="md">
            <Stack gap={4}>
              <Group justify="space-between" align="flex-start" wrap="nowrap">
                <Stack gap={4} style={{ minWidth: 0 }}>
                  <Group gap="sm">
                    <Text fw={600}>{row.name}</Text>
                    {approvalAccess.customer ? (
                      <ApprovalStatusBadge status={row.approvalStatus} />
                    ) : null}
                  </Group>
                  <Text size="sm" c="dimmed">
                    {row.phone || t('common.noPhone')} · {row.email || t('common.noEmail')}
                  </Text>
                  <Text size="sm">{t('common.balance', { amount: row.openingBalance ?? 0 })}</Text>
                </Stack>
                {access.canUpdate || access.canDelete ? (
                  <RowActions
                    editLabel={t('common.edit')}
                    deleteLabel={t('common.delete')}
                    onEdit={access.canUpdate ? () => openEdit(row) : undefined}
                    onDelete={access.canDelete ? () => setConfirmDelete(row) : undefined}
                  />
                ) : null}
              </Group>
            </Stack>
          </Paper>
        )}
        renderListItem={(row: Customer) => (
          <MobileListRow
            title={customerListTitle(row)}
            subtitle={customerListSubtitle(row, t('common.noPhone'))}
            onClick={() => navigate(`/customers/${row.id}`)}
            trailing={
              access.canUpdate || access.canDelete ? (
                <RowActions
                  editLabel={t('common.edit')}
                  deleteLabel={t('common.delete')}
                  onEdit={access.canUpdate ? () => openEdit(row) : undefined}
                  onDelete={access.canDelete ? () => setConfirmDelete(row) : undefined}
                />
              ) : undefined
            }
            showChevron={!access.canUpdate}
          />
        )}
      />

      <BottomDrawer
        opened={modalOpen}
        onClose={() => setModalOpen(false)}
        title={editing ? t('terminology.edit', { entity: terms.singular }) : t('terminology.add', { entity: terms.singular })}
      >
        <Stack gap="md">
          <CustomerForm
            value={form}
            phoneError={phoneError}
            onChange={(next) => {
              setPhoneError(null);
              setForm(next);
            }}
          />
          <Group justify="flex-end">
            <Button variant="default" onClick={() => setModalOpen(false)}>
              {t('common.cancel')}
            </Button>
            <Button
              loading={saving}
              onClick={() => void handleSave()}
              disabled={!form.name.trim() || !isCustomerPhoneValid(form)}
            >
              {t('common.save')}
            </Button>
          </Group>
        </Stack>
      </BottomDrawer>

      <ConfirmDialog
        opened={Boolean(confirmDelete)}
        onClose={() => setConfirmDelete(null)}
        onConfirm={() => void handleDelete()}
        title={t('terminology.deleteConfirm', { entity: terms.singular })}
        message={t('common.deleteMessage', { name: confirmDelete?.name ?? '' })}
        confirmLabel={t('common.delete')}
        loading={deleting}
        danger
      />
    </Stack>
  );
}
